import { addMonths, endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns";
import { ru } from "date-fns/locale";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAutoTrading } from "@/hooks/useAutoTrading";
import { useBalance } from "@/hooks/useBalance";
import { useTimeSeries } from "@/hooks/useStatistics";

import { calculateCapitalChange, summarizeTimeSeries } from "@/lib/analyticsAdapters";
import { cn } from "@/lib/cn";

import { useUserStore } from "@/store/userStore";

import { CalendarWidget, type DayStat, type MonthStat } from "@/components/calendar";
import { BalanceCard, type ProfitStatRowData, StatsListView } from "@/components/dashboard";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/Icon";
import { PaginationDots } from "@/components/ui/PaginationDots";
import { ROUTES } from "@/constants/routes";

// ============ Types ============
type ViewMode = "calendar" | "list";

const SLIDES = ["Основное", "Сделки"] as const;

// ============ Analytics Card ============
interface AnalyticsCardProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  // Calendar view props
  calendarDayStats: DayStat[];
  calendarMonthStats: MonthStat[];
  currentMonth: Date;
  onCalendarMonthChange?: (month: Date) => void;
  // List view props
  listData: ProfitStatRowData[];
  listPeriod: string;
  onListPeriodChange: (value: string) => void;
  dateFrom?: Date;
  dateTo?: Date;
  onDateFromChange: (value: Date | undefined) => void;
  onDateToChange: (value: Date | undefined) => void;
  isListLoading?: boolean;
}

const ViewModeToggle = ({
  viewMode,
  onViewModeChange,
}: {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}) => (
  <>
    <Button
      variant={viewMode === "calendar" ? "secondary" : "neutral"}
      buttonType="icon"
      size="sm"
      onClick={() => onViewModeChange("calendar")}
      icon={
        <Icon
          name="calendar"
          className="size-5"
        />
      }
    />
    <Button
      variant={viewMode === "list" ? "secondary" : "neutral"}
      buttonType="icon"
      size="sm"
      onClick={() => onViewModeChange("list")}
      icon={
        <Icon
          name="align-center"
          className="size-5"
        />
      }
    />
  </>
);

const AnalyticsCard = ({
  viewMode,
  onViewModeChange,
  calendarDayStats,
  calendarMonthStats,
  currentMonth,
  onCalendarMonthChange,
  listData,
  listPeriod,
  onListPeriodChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  isListLoading,
}: AnalyticsCardProps) => (
  <div className="flex w-full flex-col gap-2 sm:mx-auto sm:max-w-134">
    {/* Content */}
    {viewMode === "calendar" ? (
      <>
        {/* View Mode Toggle for Calendar */}
        <div className="mb-3 flex items-center justify-end gap-2">
          <ViewModeToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        </div>
        <CalendarWidget
          initialDate={currentMonth}
          dayStats={calendarDayStats}
          monthStats={calendarMonthStats}
          onMonthChange={onCalendarMonthChange}
        />
      </>
    ) : (
      <StatsListView
        listData={listData}
        period={listPeriod}
        onPeriodChange={onListPeriodChange}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={onDateFromChange}
        onDateToChange={onDateToChange}
        isLoading={isListLoading}
        headerActions={
          <ViewModeToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        }
      />
    )}
  </div>
);

// ============ Main DashboardPage ============
export const DashboardPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(() => subDays(new Date(), 7));
  const [dateTo, setDateTo] = useState<Date | undefined>(() => new Date());
  const [listPeriod, setListPeriod] = useState("7d");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [earningsPeriod, setEarningsPeriod] = useState("24h");

  // Get user from store
  const { user, isAdmin } = useUserStore();

  // Get current account UUID from auto-trading status
  const { status: autoTradingStatus } = useAutoTrading();
  const currentAccountUuid = autoTradingStatus?.account_uuid ?? undefined;

  // Fetch balance data
  const { data: balanceData, isLoading: isBalanceLoading } = useBalance();

  // Find current account balance by UUID
  const currentAccount = useMemo(() => {
    if (!currentAccountUuid) return null;
    return balanceData?.accounts.find((acc) => acc.uuid === currentAccountUuid) ?? null;
  }, [balanceData?.accounts, currentAccountUuid]);

  // Capital and fuelBalance - use current account equity if selected, otherwise total
  const capital = currentAccount
    ? parseFloat(currentAccount.usdt_equity || "0")
    : parseFloat(balanceData?.total_usdt_equity || "0");
  const fuelBalance = Number(user?.fuel ?? "0");

  const today = format(new Date(), "yyyy-MM-dd");

  // Calculate list stats period dates for StatsListView
  const { listStartDate, listEndDate } = useMemo(() => {
    return {
      listStartDate: dateFrom ? format(dateFrom, "yyyy-MM-dd") : today,
      listEndDate: dateTo ? format(dateTo, "yyyy-MM-dd") : today,
    };
  }, [dateFrom, dateTo, today]);

  // Calculate month boundaries for 3 separate queries (prev, current, next)
  const prevMonthDate = subMonths(currentMonth, 1);
  const nextMonthDate = addMonths(currentMonth, 1);

  const prevMonthStart = format(startOfMonth(prevMonthDate), "yyyy-MM-dd");
  const prevMonthEnd = format(endOfMonth(prevMonthDate), "yyyy-MM-dd");
  const currMonthStart = format(startOfMonth(currentMonth), "yyyy-MM-dd");
  const currMonthEnd = format(endOfMonth(currentMonth), "yyyy-MM-dd");
  const nextMonthStart = format(startOfMonth(nextMonthDate), "yyyy-MM-dd");
  const nextMonthEnd = format(endOfMonth(nextMonthDate), "yyyy-MM-dd");

  // Fetch stats for each month separately (parallel queries, cached independently)
  const { data: prevMonthStats } = useTimeSeries(
    prevMonthStart,
    prevMonthEnd,
    "day",
    currentAccountUuid
  );
  const { data: currMonthStats } = useTimeSeries(
    currMonthStart,
    currMonthEnd,
    "day",
    currentAccountUuid
  );
  const { data: nextMonthStats } = useTimeSeries(
    nextMonthStart,
    nextMonthEnd,
    "day",
    currentAccountUuid
  );

  // Handler for calendar month change
  const handleCalendarMonthChange = (newMonth: Date) => {
    setCurrentMonth(newMonth);
  };

  // Calculate earnings period dates
  const { earningsStartDate, earningsEndDate } = useMemo(() => {
    const now = new Date();
    switch (earningsPeriod) {
      case "7d":
        return {
          earningsStartDate: format(subDays(now, 7), "yyyy-MM-dd"),
          earningsEndDate: today,
        };
      case "30d":
        return {
          earningsStartDate: format(subDays(now, 30), "yyyy-MM-dd"),
          earningsEndDate: today,
        };
      case "90d":
        return {
          earningsStartDate: format(subDays(now, 90), "yyyy-MM-dd"),
          earningsEndDate: today,
        };
      case "24h":
      default:
        return {
          earningsStartDate: today,
          earningsEndDate: today,
        };
    }
  }, [earningsPeriod, today]);

  // Fetch earnings based on selected period
  const { data: earningsSeries } = useTimeSeries(
    earningsStartDate,
    earningsEndDate,
    "day",
    currentAccountUuid
  );

  // Fetch daily stats for StatsListView based on selected period
  const { data: listStatsData, isLoading: isListStatsLoading } = useTimeSeries(
    listStartDate,
    listEndDate,
    "day",
    currentAccountUuid
  );

  // Combine stats from all 3 months
  const dailyStats = useMemo(() => {
    const prev = Array.isArray(prevMonthStats) ? prevMonthStats : [];
    const curr = Array.isArray(currMonthStats) ? currMonthStats : [];
    const next = Array.isArray(nextMonthStats) ? nextMonthStats : [];
    return [...prev, ...curr, ...next];
  }, [prevMonthStats, currMonthStats, nextMonthStats]);

  // Slide height measurement for smooth height transition
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [slideHeight, setSlideHeight] = useState<number>(0);
  const isFirstRender = useRef(true);
  const prevSlide = useRef(currentSlide);

  // Swipe handling
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentSlide < SLIDES.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Computed values
  const userName = user?.username ?? "Пользователь";
  const capitalChange = calculateCapitalChange(capital, earningsSeries ?? []);
  const earningsSummary = summarizeTimeSeries(earningsSeries ?? []);
  const earnings = earningsSummary.profit;
  const usedFuel = earningsSummary.fuel;

  // Transform dailyStats to CalendarWidget format
  const calendarDayStats: DayStat[] = useMemo(() => {
    return dailyStats.map((s) => ({
      date: s.date,
      percent: s.capital_usdt ? (s.profit_usdt / s.capital_usdt) * 100 : 0,
      profitUsd: s.profit_usdt,
      trades: s.trades_count,
    }));
  }, [dailyStats]);

  // Calculate month stats for CalendarWidget - group by month
  const calendarMonthStats: MonthStat[] = useMemo(() => {
    // Group daily stats by year-month
    const monthGroups = new Map<string, typeof dailyStats>();

    dailyStats.forEach((day) => {
      const date = new Date(day.date);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const group = monthGroups.get(key) || [];
      group.push(day);
      monthGroups.set(key, group);
    });

    // Calculate stats for each month
    return Array.from(monthGroups.entries()).map(([key, days]) => {
      const [yearStr, monthStr] = key.split("-");
      const profitUsd = days.reduce((sum, d) => sum + d.profit_usdt, 0);
      const startingCapital = days.find((d) => (d.capital_usdt ?? 0) > 0)?.capital_usdt ?? 0;
      return {
        year: parseInt(yearStr, 10),
        month: parseInt(monthStr, 10),
        percent: startingCapital > 0 ? (profitUsd / startingCapital) * 100 : 0,
        profitUsd,
        trades: days.reduce((sum, d) => sum + d.trades_count, 0),
      };
    });
  }, [dailyStats]);

  // List data from list stats (separate query based on listPeriod)
  const listData: ProfitStatRowData[] = useMemo(() => {
    const stats = Array.isArray(listStatsData) ? listStatsData : [];
    return stats.map((s) => ({
      date: format(new Date(s.date), "d MMMM", { locale: ru }),
      profit: s.profit_usdt,
      percent: s.capital_usdt ? (s.profit_usdt / s.capital_usdt) * 100 : 0,
      trades: s.trades_count,
    }));
  }, [listStatsData]);

  // Update slide height when content changes
  useEffect(() => {
    const currentRef = slideRefs.current[currentSlide];
    if (!currentRef) return;

    const updateHeight = () => {
      setSlideHeight(currentRef.offsetHeight);
    };

    // Check if slide changed
    const slideChanged = prevSlide.current !== currentSlide;
    prevSlide.current = currentSlide;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (isFirstRender.current || !slideChanged) {
      // First render or viewMode change — update immediately
      isFirstRender.current = false;
      updateHeight();
    } else {
      // Slide changed — delay height change until after slide animation
      timeoutId = setTimeout(updateHeight, 520);
    }

    // Watch for size changes within the same slide
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(currentRef);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [currentSlide, viewMode]);

  // Handlers
  const handleChangePeriod = (value: string) => {
    setEarningsPeriod(value);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => Math.min(SLIDES.length - 1, prev + 1));
  };

  const handleListPeriodChange = (value: string) => {
    setListPeriod(value);
    const now = new Date();
    let from: Date;

    switch (value) {
      case "30d":
        from = subDays(now, 30);
        break;
      case "90d":
        from = subDays(now, 90);
        break;
      case "7d":
      default:
        from = subDays(now, 7);
        break;
    }

    setDateFrom(from);
    setDateTo(now);
  };

  const handleDateFromChange = (date: Date | undefined) => {
    setDateFrom(date);
    setListPeriod(""); // Сбрасываем пресет при ручном выборе
  };

  const handleDateToChange = (date: Date | undefined) => {
    setDateTo(date);
    setListPeriod(""); // Сбрасываем пресет при ручном выборе
  };

  return (
    <div className="bg-background flex min-h-screen flex-col overflow-hidden">
      <Header
        userName={userName}
        variant="dashboard"
        isAdmin={isAdmin}
      />
      {/* Main Content */}
      <main className="flex flex-1 flex-col px-4 py-4 pb-24 md:items-center md:justify-center md:py-8">
        <div className="flex w-full flex-col items-center gap-3 md:w-auto">
          {/* Slides Container with Navigation */}
          <div className="flex w-full items-center gap-4 md:w-auto">
            {/* Left Navigation - hidden on mobile */}
            <div className="hidden items-center gap-4 md:flex">
              <Button
                variant="neutral"
                buttonType="icon"
                size="md"
                onClick={handlePrevSlide}
                disabled={currentSlide === 0}
                className={cn(
                  "transition-opacity duration-300",
                  currentSlide === 0 && "opacity-50"
                )}
                icon={
                  <Icon
                    name="angle-left"
                    className="size-6"
                  />
                }
              />
              <div
                className={cn(
                  "rounded-mds h-53 w-3 transition-colors duration-300",
                  currentSlide === 0 ? "bg-neutral-200/2" : "bg-neutral-200/7"
                )}
              />
            </div>

            {/* Slides */}
            <div
              className="relative w-full overflow-hidden transition-[height] duration-500 ease-out md:w-134"
              style={{ height: slideHeight || "auto" }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex items-start transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {/* Slide 0: Balance */}
                <div
                  ref={(el) => {
                    slideRefs.current[0] = el;
                  }}
                  className="w-full shrink-0"
                >
                  <BalanceCard
                    capital={capital}
                    capitalChange={capitalChange}
                    earnings={earnings}
                    fuelBalance={fuelBalance}
                    usedFuel={usedFuel}
                    earningsPeriod={earningsPeriod}
                    isLoading={isBalanceLoading}
                    onChangePeriod={handleChangePeriod}
                  />
                </div>

                {/* Slide 1: Analytics */}
                <div
                  ref={(el) => {
                    slideRefs.current[1] = el;
                  }}
                  className="w-full shrink-0"
                >
                  <AnalyticsCard
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    calendarDayStats={calendarDayStats}
                    calendarMonthStats={calendarMonthStats}
                    currentMonth={currentMonth}
                    onCalendarMonthChange={handleCalendarMonthChange}
                    listData={listData}
                    listPeriod={listPeriod}
                    onListPeriodChange={handleListPeriodChange}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateFromChange={handleDateFromChange}
                    onDateToChange={handleDateToChange}
                    isListLoading={isListStatsLoading}
                  />
                </div>
              </div>
            </div>

            {/* Right Navigation - hidden on mobile */}
            <div className="hidden items-center gap-4 md:flex">
              <div
                className={cn(
                  "rounded-mds h-53 w-3 transition-colors duration-300",
                  currentSlide === 1 ? "bg-neutral-200/2" : "bg-neutral-200/7"
                )}
              />
              <Button
                variant="neutral"
                buttonType="icon"
                size="md"
                onClick={handleNextSlide}
                disabled={currentSlide === SLIDES.length - 1}
                className={cn(
                  "transition-opacity duration-300",
                  currentSlide === SLIDES.length - 1 && "opacity-50"
                )}
                icon={
                  <Icon
                    name="angle-right"
                    className="size-6"
                  />
                }
              />
            </div>
          </div>

          {/* Bottom section */}
          <div className="flex w-full flex-col items-center gap-6 md:max-w-134 md:pt-0">
            {/* Actions Section - only on first slide */}
            <div
              className={cn(
                "grid w-full transition-[grid-template-rows,opacity] duration-500",
                currentSlide === 0
                  ? "grid-rows-[1fr] opacity-100"
                  : "pointer-events-none grid-rows-[0fr] opacity-0"
              )}
            >
              {/* <div className="overflow-hidden">
                <Button
                  variant="neutral"
                  size="lg"
                  className="w-full"
                  onClick={handleSelectApi}
                  icon={
                    <Icon
                      name="diamond"
                      className="size-6"
                    />
                  }
                >
                  Выбрать API
                </Button>
              </div> */}
            </div>

            {/* Pagination */}
            <PaginationDots
              total={SLIDES.length}
              current={currentSlide}
              labels={SLIDES}
              onSelect={setCurrentSlide}
            />
          </div>
        </div>
      </main>
      <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
        <Button
          asChild
          variant="neutral"
        >
          <Link to={ROUTES.analytics}>
            Аналитика{" "}
            <Icon
              name="angle-up"
              className="size-5"
            />
          </Link>
        </Button>
      </div>
    </div>
  );
};
